import type {
  HubDeletedResource,
  HubResource,
} from '@robovision/hub'
import {
  arrayToObject,
  filterObject,
  HttpStatusCode,
} from '@robovision/rvai-utils'
import { defineStore } from 'pinia'
import {
  reactive,
  readonly,
  toRefs,
} from 'vue'
import { hubApi } from '~services/api'
import { apiFetch } from '~utils/api'


interface ModelResourceFilters {
  definitionId?: string
  onlyValidModels?: boolean
  projectId?: string
  sourceJobId?: string
}

interface ResourcesState {
  blobs: Record<string, HubResource>
  models: Record<string, HubResource>
}

export const useResourcesStore = defineStore('resources', () => {
  const state = reactive<ResourcesState>({
    blobs: {},
    models: {},
  }) as ResourcesState

  const getters = {
    getBlobResource (resourceId: string): HubResource | undefined {
      return state.blobs[resourceId]
    },

    getModelResource (resourceId: string): HubResource | undefined {
      const model = state.models[resourceId]
      return model?.valid ? model : undefined
    },

    getModelResourcesFiltered ({
      definitionId,
      projectId,
      sourceJobId,
    }: ModelResourceFilters): HubResource[] {
      if (!definitionId && !projectId && !sourceJobId) return []

      return Object.values(state.models)
        .filter(m =>
          (!definitionId || m.definitionId === definitionId) &&
          (!projectId || m.projectId === projectId) &&
          (!sourceJobId || m.sourceJobId === sourceJobId) &&
          m.valid)
    },
  }

  const actions = {
    deleteModelBySourceJobId (sourceJobId: string): void {
      state.models = filterObject(state.models, (m) => m.sourceJobId !== sourceJobId)
    },

    handleDeleteResource (deletedResources: HubDeletedResource) {
      if (state.models[deletedResources.id]) {
        delete state.models[deletedResources.id]
      }
      if (state.blobs[deletedResources.id]) {
        delete state.blobs[deletedResources.id]
      }
    },

    async loadModel (resourceId: string, allowNotFound?: boolean): Promise<void> {
      const { data } = await apiFetch({
        fn: () => hubApi.resources.getModelResourceInfo(resourceId, allowNotFound ? {
          validateStatus: status => [HttpStatusCode.OK, HttpStatusCode.NOT_FOUND].includes(status),
        } : {}),
        key: `getModelResourceInfo-${resourceId}`,
      })

      state.models = {
        ...state.models,
        [data.id]: data,
      }
    },

    async loadModels ({
      definitionId,
      jobId,
      projectId,
    }: {
      definitionId?: string
      jobId?: string
      projectId?: string
    } = {}): Promise<string[]> {
      const { data } = await apiFetch({
        fn: () => hubApi.resources.listModelResources({
          filter: [
            (definitionId && `definitionId:${definitionId}`),
            (jobId && `sourceJobId:${jobId}`),
            (projectId && `projectId:${projectId}`),
          ].filter(Boolean),
        }),
        key: `listModelResources-${definitionId}-${jobId}-${projectId}`,
      })

      state.models = {
        ...state.models,
        ...arrayToObject(data.items, r => r.id, r => r),
      }

      return data.items.map(r => r.id)
    },

    async renameModel (resourceId: string, name: string): Promise<void> {
      const model = state.models[resourceId]
      const { data } = await hubApi.resources.overrideModelResourceMetadata(resourceId, {
        metadata: model?.metadata,
        name,
      })
      state.models = {
        ...state.models,
        [data.id]: data,
      }
    },

    setBlobs (resources: HubResource[]) {
      state.blobs = {
        ...state.blobs,
        ...arrayToObject(resources, r => r.id, r => r),
      }
    },

    setModels (resources: HubResource[]) {
      state.models = {
        ...state.models,
        ...arrayToObject(resources, r => r.id, r => r),
      }
    },

    setState (params: Partial<typeof state>): void {
      Object.assign(state, params)
    },
  }

  return {
    ...toRefs(readonly(state)),
    ...getters,
    ...actions,
  }
})
